// src/components/padrekko/qc/QcCountsSummary.jsx
//
// The counts above the findings list, split by SCOPE. It replaces four stacked
// bars (pad completion, pad review, case completion, case review) that mixed
// pad-scoped and case-scoped figures in the pad's working area.
//
//   PadCounts       — pad-scoped. Stays in the inspector panel.
//   CaseCountsLine  — case-scoped. One line; its home is the case bar.
//
// THE SAME NUMBERS, FEWER ROWS. Nothing here counts anything new: completion is
// the same countCompletion CompletionCountsBar uses (completionCounts.js), and review coverage is the
// server's reviewCounts.js figures, passed through as ReviewCountsBar receives
// them. What moved out of the visible text moved into a `title`, never away:
//   - pad `seen`: into the tooltip of the reviewed figure. It is the weakest
//     signal and is not part of `reviewed` (ReviewCountsBar.jsx:23).
//   - case validated / revised / seen / removed: into the case line's tooltip.
// QcCountsSummary.test.jsx asserts every number the old bars showed is still
// present, reading the old components' own output as the reference.
//
// NOT CHANGED HERE, deliberately: completion counts removed findings and review
// excludes them (REM-442). Changing that inside a consolidation whose control
// asserts the numbers are unchanged would alter the reference it compares to.

import CompletionCountsBar from "./CompletionCountsBar";
import { countCompletion } from "./completionCounts";

/**
 * Review coverage with the same null rules as ReviewCountsBar (:39, :44): no
 * counts, or a pad with nothing active and nothing removed, makes no claim.
 */
function reviewVisible(counts) {
  if (!counts) return false;
  return !!(counts.active || counts.removed);
}

const atStageSuffix = (stage) => (stage ? ` at ${stage}` : "");

/** Pad-scoped: the findings title with completion, then one review line. */
export function PadCounts({ findingsCount, findings, reviewCounts, stage = null, className = "" }) {
  const showReview = reviewVisible(reviewCounts);
  const c = reviewCounts ?? {};
  const atStage = atStageSuffix(stage);
  return (
    <div className={className} data-qc-counts="pad">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary">
          Findings · {findingsCount}
        </div>
        <CompletionCountsBar findings={findings} />
      </div>
      {showReview && (
        <div className="flex items-center gap-2 text-[9px] tabular-nums flex-wrap">
          <span
            className={c.reviewed === c.active && c.active > 0 ? "text-emerald-400" : "text-slate-300"}
            title={`${c.reviewed} of ${c.active} active findings reviewed${atStage} — validated or revised by a person. ${c.seen} seen: someone looked; set automatically on dismiss and not counted as reviewed.`}
          >
            {c.reviewed}/{c.active} <span className="text-fg-secondary">reviewed</span>
          </span>
          <span className="text-slate-700">·</span>
          <span className="text-emerald-400/80" title={`A person affirmed the analysis stands, changing nothing${atStage}`}>
            {c.validated} <span className="text-fg-secondary">validated</span>
          </span>
          <span className="text-sky-400/80" title={`A person edited, reanalyzed or removed content${atStage}`}>
            {c.revised} <span className="text-fg-secondary">revised</span>
          </span>
          {c.removed > 0 && (
            <span className="text-rose-400/70" title="Dropped from the case during review. Outside both the numerator and the denominator.">
              {c.removed} <span className="text-fg-secondary">removed</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Case-scoped, one line: completion across every pad, then review coverage.
 * Renders null when neither half has anything to say, matching the two bars it
 * replaces.
 */
export function CaseCountsLine({ findings, reviewCounts, stage = null, className = "" }) {
  const comp = countCompletion(findings);
  const showComp = comp.total > 0;
  const showReview = reviewVisible(reviewCounts);
  if (!showComp && !showReview) return null;
  const c = reviewCounts ?? {};
  const atStage = atStageSuffix(stage);
  const breakdown =
    `${c.validated} validated · ${c.revised} revised · ${c.seen} seen` +
    (c.removed > 0 ? ` · ${c.removed} removed` : "") + atStage;
  return (
    <div className={`flex items-center gap-2 text-[11px] tabular-nums whitespace-nowrap ${className}`} data-qc-counts="case">
      <span className="font-mono uppercase tracking-widest text-fg-secondary">Case</span>
      {showComp && (
        <>
          <span className="text-emerald-400" title="Completed">● {comp.completed}</span>
          <span className="text-amber-400" title="Not completed">● {comp.not_completed}</span>
          <span className="text-red-400" title="Pending">● {comp.pending}</span>
          <span className="text-fg-secondary">/ {comp.total}</span>
        </>
      )}
      {showComp && showReview && <span className="text-slate-700">·</span>}
      {showReview && (
        <span
          className={c.reviewed === c.active && c.active > 0 ? "text-emerald-400" : "text-slate-300"}
          title={`${c.reviewed} of ${c.active} active findings reviewed across the case — ${breakdown}`}
        >
          {c.reviewed}/{c.active} <span className="text-fg-secondary">reviewed</span>
        </span>
      )}
    </div>
  );
}
